import React, { useEffect, useMemo } from "react";
import { Button, Card, DatePicker, Input, message, Modal, Tag, Tooltip } from "antd";
import {
  PhoneOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  DollarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeads, fetchLeadStatus, updateLeadStatus } from "../leadThunk";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { createTasksApi, fetchTasksApi, saveConversationApi, updateLeadMetaApi } from "../../../../apiservice/crm";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDayjsToUtc } from "../../../../lib/functions";
import { resolveDialableNumber } from "../../../../lib/phone";
import "../../crmSharedStyle.css";

const PipelineView = ({setCreateModal}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list: leads, statusList } = useSelector((s) => s.leads);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteModal, setNoteModal] = useState(false);
  const [followModal, setFollowModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [followDate, setFollowDate] = useState(null);
  const [convertModal, setConvertModal] = useState(false);
  const [dealValue, setDealValue] = useState("");
  const [followUpDate, setFollowUpDate] = useState(null);

  const load = async () => {
      setLoading(true);
      const res = await fetchTasksApi({});
      setTasks(res.data || []);
      setLoading(false);
    };
    
    useEffect(() => {
      load();
      
    }, []);
  

  useEffect(() => {
  if (!statusList?.length) {
    dispatch(fetchLeadStatus());
  }
}, [statusList?.length]);

  // 🔥 Group leads by status_id
  const grouped = useMemo(() => {
  const map = {};

  statusList?.forEach((s) => {
    map[s.id] = [];
  });

  leads.forEach((lead) => {
    if (!map[lead.status_id]) {
      map[lead.status_id] = [];
    }
    map[lead.status_id].push(lead);
  });

  return map;
}, [leads, statusList]);

  // 🔥 Drag handler
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    const newStatusId = destination.droppableId;

    if (source.droppableId === destination.droppableId) return;

    try {
      await updateLeadMetaApi({
        lead_id: draggableId,
        status_id: newStatusId,
      });

      dispatch({
        type: "leads/updateLocalStatus",
        payload: {
            id: draggableId,
            status_id: newStatusId,
        },
        });
      
    } catch (err) {
      console.error(err);
    }
  };

  const openNoteModal = (lead) => {
  setSelectedLead(lead);
  setNoteModal(true);
};

const openFollowUpModal = (lead) => {
  setSelectedLead(lead);
  setFollowModal(true);
};

const convertLead = async (lead) => {  
  try {
    await updateLeadStatus({
      id: lead.id,
      action: "convert",
      source: "account",
    });
    dispatch(fetchLeads());
  } catch {
    console.error("Convert failed");
  }
};

const handleConvertSubmit = async () => {
  if (!dealValue) {
    return message.warning("Enter deal value");
  }

  if (!followUpDate) {
    return message.warning("Select follow-up date");
  }

  try {
    await dispatch(
      updateLeadStatus({
        id: selectedLead.id,
        action: "convert",
        // source: selectedLead.source,
        source: "account",
        dealValue,
        followUpDate,
      })
    ).unwrap();

    message.success("Converted → Deal created 🚀");

    setConvertModal(false);
    setDealValue("");
    setFollowUpDate(null);

    dispatch(fetchLeads());
    navigate("/crm/deals");
  } catch {
    message.error("Conversion failed ❌");
  }
};

  const taskMap = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (!map[t.lead_id]) map[t.lead_id] = [];
      map[t.lead_id].push(t);
    });
    return map;
  }, [tasks]); 
  

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 10 }}>
        {statusList?.map((status) => (
          <Droppable droppableId={String(status.id)} key={status.id}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                minWidth: 300,
                background: "#fafafa",
                padding: 12,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                
                }}  
              >
                {/* COLUMN HEADER */}
                <div style={{ marginBottom: 10,
                    position: "sticky",
                    top: 0,
                    background: "#fafafa",
                    zIndex: 1,
                    paddingBottom: 8
                 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <Tooltip title={status?.name}>
                      <Tag
                        color={status.color}
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          margin: 0,
                        }}
                      >
                        {status.name}
                      </Tag>
                    </Tooltip>
                    <span style={{ color: "#8c8c8c", fontSize: 13, fontWeight: 600 }}>
                      {grouped[status.id]?.length || 0}
                    </span>
                  </div>
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModal({ open: true, statusId: status.id })}
                  >
                    Add Lead
                  </Button>
                </div>

                {/* CARDS */}
                {grouped[status.id]?.map((lead, index) => {
                const leadTasks = taskMap[lead.id] || [];
                const hasOverdue = leadTasks.some(t => t.bucket === "overdue");
                const overdueCount = leadTasks?.filter((t) => t.bucket === "overdue").length || 0;

                return (
                  <Draggable
                    key={lead.id}
                    draggableId={String(lead.id)}
                    index={index}
                  >
                    {(provided) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: 10,
                          borderRadius: 12,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          cursor: "grab",
                          transition: "0.2s",
                        }}
                        styles={{ body: { padding: 12 } }}
                      >
                        <b>{lead.name}</b>

                        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                          {lead.phone ? resolveDialableNumber(lead.phone, lead.country) : "-"}
                        </div>

                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <Tooltip title={lead.source}>
                            <Tag
                              color="blue"
                              style={{
                                maxWidth: "190px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                margin: 0,
                              }}
                            >
                              {lead.source}
                            </Tag>
                          </Tooltip>

                          {overdueCount > 0 && (
                            <Tag color="red" style={{ margin: 0 }}>
                              {overdueCount} Overdue Task{overdueCount > 1 ? "s" : ""}
                            </Tag>
                          )}
                        </div>

                        {/* ACTIONS */}
                        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                          <Tooltip title="Call">
                            <Button
                              size="small"
                              shape="circle"
                              icon={<PhoneOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `tel:${resolveDialableNumber(lead.phone, lead.country)}`;
                              }}
                            />
                          </Tooltip>

                          <Tooltip title="Add note">
                            <Button
                              size="small"
                              shape="circle"
                              icon={<FileTextOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openNoteModal(lead);
                              }}
                            />
                          </Tooltip>

                          <Tooltip title="Schedule follow-up">
                            <Button
                              size="small"
                              shape="circle"
                              icon={<ClockCircleOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openFollowUpModal(lead);
                              }}
                            />
                          </Tooltip>

                          <Tooltip title="Convert to deal">
                            <Button
                              size="small"
                              shape="circle"
                              type="primary"
                              icon={<ThunderboltOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLead(lead);
                                setConvertModal(true);
                              }}
                            />
                          </Tooltip>
                        </div>
                      </Card>
                    )}
                  </Draggable>
                );
              })}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ))}
      </div>

      <Modal
        open={noteModal}
        onCancel={() => setNoteModal(false)}
        onOk={async () => {
          await saveConversationApi({
            customer_id: selectedLead.id,
            text: noteText,
          });
          setNoteModal(false);
          setNoteText("");
        }}
        title={`Add Note — ${selectedLead?.name || ""}`}
        className="crm-modal-responsive"
      >
        <div className="crm-field-label">
          <FileTextOutlined />
          Note
        </div>
        <Input.TextArea
          rows={4}
          placeholder="What happened on this lead?"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
      </Modal>

      <Modal
        open={followModal}
        onCancel={() => setFollowModal(false)}
        onOk={async () => {
          await createTasksApi({
            lead_id: selectedLead.id,
            type: "followup",
            // due_date: followDate.format("YYYY-MM-DD HH:mm:ss"),
            due_date: formatDayjsToUtc(followDate),
            remarks: "Pipeline follow-up",
          });
          setFollowModal(false);
          setFollowDate(null);
        }}
        title={`Schedule Follow-up — ${selectedLead?.name || ""}`}
        className="crm-modal-responsive"
      >
        <div className="crm-field-label">
          <CalendarOutlined />
          Follow-up date &amp; time
        </div>
        <DatePicker
          showTime
          style={{ width: "100%" }}
          value={followDate}
          onChange={(v) => setFollowDate(v)}
        />
      </Modal>

      <Modal
        open={convertModal}
        onCancel={() => setConvertModal(false)}
        title={`Convert Lead — ${selectedLead?.name || ""}`}
        footer={null}
        className="crm-modal-responsive"
      >
        <div className="crm-form-section">
          <div className="crm-field-label">
            <DollarOutlined />
            Deal value
          </div>
          <Input
            placeholder="Deal value"
            value={dealValue}
            onChange={(e) => setDealValue(e.target.value)}
          />
        </div>

        <div className="crm-form-section">
          <div className="crm-field-label">
            <CalendarOutlined />
            Follow-up date
          </div>
          <DatePicker
            style={{ width: "100%" }}
            value={followUpDate}
            onChange={(d) => setFollowUpDate(d)}
          />
        </div>

        <Button
          type="primary"
          block
          size="large"
          onClick={handleConvertSubmit}
        >
          Convert
        </Button>
      </Modal>
    </DragDropContext>
  );
};

export default PipelineView;